<template>
  <section
    aria-labelledby="store-locator-heading"
    class="flex flex-col gap-6 px-6 py-8 md:flex-row md:items-start md:justify-center lg:px-16 xl:px-24"
  >
    <div class="flex flex-col gap-6 md:shrink-0">
      <div class="flex flex-col gap-5">
        <h1 id="store-locator-heading" class="text-[44px] font-medium leading-[50px] tracking-[-1px] text-neutral-900">
          {{ $t('storeLocator.title') }}
        </h1>

        <nav :aria-label="$t('storeLocator.breadcrumb')">
          <ol class="flex items-center gap-2">
            <li>
              <NuxtLinkLocale
                to="/"
                class="flex items-center gap-1 text-sm font-normal leading-5 text-neutral-500 hover:text-neutral-900"
              >
                <component :is="IconHome" class="size-5 fill-neutral-500" aria-hidden="true" />
              </NuxtLinkLocale>
            </li>
            <li>
              <span aria-current="page" class="text-sm font-medium leading-5 text-neutral-500">
                {{ $t('breadcrumb.home') }}
              </span>
            </li>
            <li aria-hidden="true">
              <component :is="IconBullet" class="size-5 fill-neutral-500" />
            </li>
            <li>
              <span aria-current="page" class="text-sm font-medium leading-5 text-neutral-900">
                {{ $t('storeLocator.breadcrumb') }}
              </span>
            </li>
          </ol>
        </nav>
      </div>

      <StoreLocatorList
        :stores="stores"
        :total="total"
        :current-page="currentPage"
        :total-pages="totalPages"
        :is-loading="storeFetchState === FetchState.IN_PROGRESS"
        :active-store-id="activeStoreId"
        :active-tab="activeTab"
        @search="handleSearch"
        @geolocate="handleGeolocate"
        @select="handleStoreSelect"
        @tab-change="activeTab = $event"
        @page-change="handlePageChange"
      />
    </div>

    <div v-if="activeTab === 'map'" class="md:hidden">
      <StoreLocatorMap
        :stores="stores"
        :active-store-id="activeStoreId"
        :user-position="userPosition"
        class="h-[60vh] w-full overflow-hidden rounded-2xl"
        @select="handleStoreSelect"
      />
    </div>

    <div class="hidden flex-1 md:block">
      <StoreLocatorMap
        :stores="stores"
        :active-store-id="activeStoreId"
        :user-position="userPosition"
        class="h-[773px] w-full overflow-hidden rounded-2xl"
        @select="handleStoreSelect"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { IconBullet, IconHome } from '#components';
import { useDebounceFn } from '@vueuse/core';

import type { Locale } from '~/stores/app/types';

import { STORES_PAGE_LIMIT, useStoreStore } from '~/stores/store/storeStore';
import { FetchState } from '~/utils/common';

const { locale } = useI18n();
const storeStore = useStoreStore();
const { currentPage, storeFetchState, stores, total, totalPages } = storeToRefs(storeStore);

const activeStoreId = ref<string | undefined>(undefined);
const activeTab = ref<'list' | 'map'>('list');
const keywords = ref('');
const userPosition = ref<{ lat: number; lng: number } | undefined>(undefined);

const { data: _data, refresh } = await useAsyncData(`store-locator-${locale.value}`, () =>
  storeStore.fetchStores({ limit: STORES_PAGE_LIMIT, locale: locale.value as Locale, page: 1 }),
);

watch(locale, () => {
  keywords.value = '';
  refresh();
});

const debouncedSearch = useDebounceFn(async (search: string): Promise<void> => {
  const trimmed = search.trim();
  const nextKeywords = trimmed.length >= 3 ? trimmed : '';
  await storeStore.fetchStores({
    ...(nextKeywords ? { keywords: nextKeywords } : {}),
    limit: STORES_PAGE_LIMIT,
    locale: locale.value as Locale,
    page: 1,
  });
  if (storeFetchState.value === FetchState.SUCCEEDED) {
    keywords.value = nextKeywords;
  }
}, 300);

const handleSearch = (search: string): void => {
  activeStoreId.value = undefined;
  debouncedSearch(search);
};

const handleGeolocate = ({ lat, lng }: { lat: number; lng: number }): void => {
  activeStoreId.value = undefined;
  keywords.value = '';
  userPosition.value = { lat, lng };
  storeStore.fetchStores({ lat, limit: STORES_PAGE_LIMIT, lng, locale: locale.value as Locale, page: 1 });
};

const handlePageChange = (page: number): void => {
  storeStore.fetchStores({
    ...(keywords.value ? { keywords: keywords.value } : {}),
    limit: STORES_PAGE_LIMIT,
    locale: locale.value as Locale,
    page,
  });
};

const handleStoreSelect = (storeId: string): void => {
  activeStoreId.value = storeId || undefined;
  if (storeId) {
    nextTick(() => {
      const cardEl = document.getElementById(`store-locator-card-${storeId}`);
      cardEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }
};
</script>
